import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IDeposit, ITransaction } from '../../interfaces/i-api-deposit'
import { ExpandableComponent } from '../../components/expandable/Expandable'
import { Transaction } from '../../components/transaction/Transaction'
import Tooltip from '../../components/Tooltip'
import {
	getCurrency,
	checkIsMobile,
	getErrorWithFullMessage,
	stringifyNum,
	logError,
} from 'exports'
import { getDataDeposit, getDepositInfo } from '../../utils/countryDataHelper'
import customerDataApiService from '../../services/customerDataApiService'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Alert } from 'react-bootstrap'
import AppConfigContext from 'contexts/azureAppConfig'
import { Toggle } from '../../constants/enums/ToggleTypes'

interface IProps {
	onChangeValues: any
}

function Deposits({ onChangeValues }: IProps) {
	const { t } = useTranslation()
	const [numberOfTransactions, setNumberOfTransactions] = useState(10)
	const [isMobile] = useState(checkIsMobile())
	const [data, setData] = useState<IDeposit[]>([])
	const [show, setShow] = useState(true)
	const [error, setError] = useState<Error>()
	const { toggle } = useContext(AppConfigContext)

	const getHeader = (depositType: string, balance: number, ocr: string) => {
		return (
			<>
				<div className="d-flex m-3 header-text header-column-deposit">
					{depositType.toLowerCase().startsWith('f1')
						? depositType.replace('F1 ', '') + ' (' + ocr + ')'
						: depositType + ' (' + ocr + ')'}
				</div>
				<div className="d-flex header-text header-column-deposit">
					{stringifyNum(balance, {
						currency: getCurrency(),
						style: 'currency',
					})}
				</div>
			</>
		)
	}

    const $primarydarkbrown = '#6B2900';

	const $primarybronze1 ='#de996f';
	
	const hasData = () => Array.isArray(data) && data.length > 0

	const isLoading = () => !hasData() && show

	const color1 = '#c87d5a' // Bronze red
	const color2 = '#eea87e' // Bronze light
	const getColor = (key: number) => (key % 2 === 0 ? color1 : color2)

	function getChildDataWithProducts(deposits: IDeposit[]) {
		return (
			<>
				{deposits.map((deposit, key) => {
					return (
						<div
							key={key}
							className="d-flex flex-column column-wrap">
							<ExpandableComponent
								key={key}
								id={key}
								children={getChildData(deposit, getColor(key))}
								onClick={() => {}}
								content={getHeader(
									deposit.type,
									deposit.balance,
									deposit.ocr,
								)}
								backgroundColor={'#FFFFFF'}
								color={getColor(key)}
							/>
						</div>
					)
				})}
			</>
		)
	}

	function getChildData(deposit: IDeposit, color: string) {
		const transactions = deposit
			.transactions!.slice(0, numberOfTransactions)
			.map((transaction: ITransaction, key: number) => (
				<Transaction key={key} id={key} item={transaction} />
			))

		return (
			<div
				className={
					isMobile
						? 'home-page-container-collapse'
						: 'd-flex home-page-container-collapse'
				}
				style={{
					border: '1px solid #E0E0E0',
					borderLeft: `3px solid ${color}`,
				}}>
				<div
					style={{
						width: isMobile ? '100%' : '45%',
					}}
					className="pb-3 pt-3 deposit-info">
					<div className="pt-3 pl-2">
						<b>{t('AccountInformation')}</b>
					</div>

					{getDataDeposit(deposit, t).map((item, key) => {
						return (
							<div
								key={key}
								className="d-flex flex-row justify-content-between my-3">
								<div className="pl-2 d-flex">
									{t(item.text)}
									{item.tooltip! && (
										<Tooltip item={item.tooltip} />
									)}
								</div>
								<div className="pr-2 text-right">
									{item.value}
								</div>
							</div>
						)
					})}
					<div className="pt-4 pl-2">
						<b>{t('DepositInformation')}</b>
					</div>

					{getDepositInfo(deposit, t).map((item, key) => {
						return (
							<div
								key={key}
								className="d-flex flex-row justify-content-between my-3">
								<div className="pl-2 d-flex">
									{t(item.text)}
									{item.tooltip! && (
										<Tooltip item={item.tooltip} />
									)}
								</div>
								<b>
									<div className="pr-2 text-right">
										{item.value}
									</div>
								</b>
							</div>
						)
					})}
				</div>
				<div
					style={{
						width: isMobile ? '100%' : '55%',
						height: isMobile ? '' : '100%',
						overflow: 'auto',
						borderLeft: '1px solid rgb(238, 238, 238)',
					}}
					className={isMobile ? 'pt-4' : 'pt-3'}>
					<div className={isMobile ? 'p-2' : 'py-3 pl-3'}>
						<b>{t('Transactions')}</b>
					</div>

					<table className="table table-striped transactions">
						<thead>
							<tr>
								<th scope="col">{t('Date')}</th>
								<th scope="col">{t('Type')}</th>
								<th scope="col">{t('Amount')}</th>
							</tr>
						</thead>
						<tbody>{transactions}</tbody>
					</table>

					{deposit.transactions.length > 10 &&
						numberOfTransactions < deposit.transactions.length && (
							<div
								onClick={() =>
									setNumberOfTransactions(
										numberOfTransactions + 20,
									)
								}
								className="d-flex flex-row read-more mb-4 mt-3 pointer">
								<span></span>
								<span
									style={{
										backgroundColor: $primarybronze1,
										color: $primarydarkbrown,
									}}>
									{t('ShowMore')}
								</span>
								<span></span>
							</div>
						)}
				</div>
			</div>
		)
	}

	async function getDeposits() {
		try {
			let data = await customerDataApiService.getDepositsData(t)
			setData(data as IDeposit[])
			if (data.length === 0) setShow(false)
		} catch (error: any) {
			logError(error)
			setError(getErrorWithFullMessage(error))
			setShow(false)
		}
	}

	useEffect(() => {
		onChangeValues(show, error)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, error])

	useEffect(() => {
		toggle.includes(Toggle.deposits) ? getDeposits() : setShow(false)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<div className={'mb-2'}>
			{hasData() || isLoading() ? (
				<ExpandableComponent
					key={'Deposits'}
					id={1}
					children={getChildDataWithProducts(data)}
					onClick={() => {}}
					backgroundColor={'white'}
					color={'#e9ecef'}
					content={
						<div className="d-flex ml-4" style={{ width: '100%' }}>
							<div className="d-flex">
								<FontAwesomeIcon
									style={{ color: '#444444' }}
									icon={'piggy-bank'}
									className="ml-1 home-icon"
								/>
								<div className="ml-3">
									{t('SavingAccounts')}
								</div>
							</div>
							{isLoading() && (
								<div className="loading d-flex ml-auto mr-5 mb-2"></div>
							)}
						</div>
					}
					expanded={hasData()}
					chevron={!isLoading()}
				/>
			) : (
				error && (
					<Alert variant="danger" className="mt-2 mb-4">
						{error?.message}
					</Alert>
				)
			)}
		</div>
	)
}

export default Deposits

